import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { fetchTenderV2, reextractTenderV2, setTenderSkillActiveV2 } from '../../api/v2/tenders';
import { fetchCandidatesV2, restoreCandidateV2 } from '../../api/v2/candidates';
import { fetchSwapOptionsV2, fetchTeamV2 } from '../../api/v2/teams';
import type {
  CandidateAPIV2,
  CriterionAPIV2,
  PositionAPIV2,
  TenderAPIV2,
  TenderRawEntryAPIV2,
  TeamAPIV2,
  TeamSwapOptionAPIV2,
} from '../../types';
import NotFound from '../../components/NotFound';
import { SkillRow } from '../../components/SkillChips';
import StatTile from '../../components/StatTile';
import { useTranslation } from 'react-i18next';
import FulfilmentRail from '../../components/FulfilmentRail';
import CandidateCard from '../../components/CandidateCard';
import CriteriaRail from '../../components/CriteriaRail';
import AddCriterionTile from '../../components/AddCriterionTile';

/**
 * Format an ISO LocalDate string (e.g. "2026-05-28") as a browser-locale date.
 * Returns "--" for null/invalid input.
 *
 * Important: we parse the parts manually rather than `new Date(iso)` to avoid
 * the JS Date constructor's UTC interpretation of date-only strings, which can
 * shift the day in non-UTC timezones.
 */
function formatDeadline(iso: string | null): string {
  if (!iso) return '--';
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return '--';
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

// Seat-line band chips in the roles rail — same palette as CandidateCard.
const SEAT_BAND_CLS: Record<string, string> = {
  COMPELLING: 'bg-green-100 text-green-800',
  CREDIBLE: 'bg-blue-50 text-blue-700',
  ARGUABLE: 'bg-amber-50 text-amber-700',
  ELIMINATED: 'bg-rose-50 text-rose-700',
};

export default function TenderOverview() {
  const { t } = useTranslation();
  const { tenderId } = useParams<{ tenderId: string }>();
  const [tender, setTender] = useState<TenderAPIV2 | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);
  const [candidates, setCandidates] = useState<CandidateAPIV2[] | null>(null);
  const [candidatesLoading, setCandidatesLoading] = useState(true);
  // Mid-column tab: defaults to Candidates (matching outcome); Tender shows
  // the ingested raw content so the recruiter can sanity-check the reading.
  const [midTab, setMidTab] = useState<'candidates' | 'tender'>('candidates');
  // Re-extraction is destructive (regenerates extracted criteria/skills) and
  // async — the backend returns immediately and the new results land minutes
  // later, so we surface a status note rather than auto-refreshing.
  const [reextracting, setReextracting] = useState(false);
  const [reextractNote, setReextractNote] = useState<string | null>(null);
  // Selected role (position ordinal) — filters the criteria rail to that role's
  // requirements and responsibilities. null shows all. Only settable once
  // attachment has produced links.
  const [selectedOrdinal, setSelectedOrdinal] = useState<number | null>(null);
  const [team, setTeam] = useState<TeamAPIV2 | null>(null);
  const [swapOptions, setSwapOptions] = useState<TeamSwapOptionAPIV2[]>([]);
  const [teamNonce, setTeamNonce] = useState(0);

  useEffect(() => {
    if (!tenderId) return;
    let cancelled = false;
    setLoading(true);
    setNotFound(false);

    fetchTenderV2(tenderId)
      .then((data) => {
        if (cancelled) return;
        if (data === null) {
          setNotFound(true);
          setTender(null);
        } else {
          setTender(data);
          // Land on the first (primary) detected position rather than the
          // tender-wide view. Seeds the selection once per tender load.
          const detected = Array.isArray(data.positions) ? data.positions : [];
          const firstOrdinal = detected.length > 0
            ? Math.min(...detected.map((p) => p.ordinal))
            : 0;
          setSelectedOrdinal(firstOrdinal);
        }
      })
      .catch(() => {
        if (!cancelled) setTender(null);
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [tenderId]);

  // Candidates: tender-wide, or ranked/banded for the selected role. Refetched
  // whenever the role selection changes — per-role is what surfaces specialists
  // on a multi-role tender (per_position_matching_spec).
  useEffect(() => {
    if (!tenderId) return;
    let cancelled = false;
    setCandidatesLoading(true);
    setCandidates(null);
    fetchCandidatesV2(tenderId, selectedOrdinal ?? undefined)
      .then((data) => { if (!cancelled) setCandidates(data?.candidates ?? []); })
      .catch(() => { if (!cancelled) setCandidates([]); })
      .finally(() => { if (!cancelled) setCandidatesLoading(false); });
    return () => { cancelled = true; };
  }, [tenderId, selectedOrdinal]);

  // The proposed team (team_proposal_spec §3–§7). Independent of the selected
  // role: it spans every position, and the first read seeds the stored
  // selection so later reads return the recruiter's team rather than a fresh
  // proposal (§7). A null result means the tender is unknown — a tender with no
  // team to propose still answers, via `outcome`.
  useEffect(() => {
    if (!tenderId) return;
    let cancelled = false;
    fetchTeamV2(tenderId)
      .then((data) => { if (!cancelled) setTeam(data); })
      .catch(() => { if (!cancelled) setTeam(null); });
    return () => { cancelled = true; };
  }, [tenderId, teamNonce]);

  // The team lens on the selected role's list (§6): what each candidate would
  // cover, how scarce it is, and what a swap gains or loses. Laid over the
  // ranked list rather than replacing it — the list stays the swap list.
  useEffect(() => {
    if (!tenderId || selectedOrdinal === null || team?.outcome !== 'TEAM_PROPOSED') {
      setSwapOptions([]);
      return;
    }
    let cancelled = false;
    fetchSwapOptionsV2(tenderId, selectedOrdinal)
      .then((data) => { if (!cancelled) setSwapOptions(data); })
      .catch(() => { if (!cancelled) setSwapOptions([]); });
    return () => { cancelled = true; };
  }, [tenderId, selectedOrdinal, team?.outcome, teamNonce]);

  // Refetch just the candidate list (after an analysis run, or a role reselect),
  // honoring the current role selection.
  /**
   * Who currently holds each candidate, derived from the team we already have.
   * The backend is not asked "is this person seated elsewhere" — the answer is
   * already on the page, and seat uniqueness is an invariant of the saved team
   * rather than a filter on any list (§6).
   */
  const seatHolders = new Map<string, { ordinal: number; title: string | null }>();
  const seatedNames = new Map<string, string>();
  (team?.seats ?? []).forEach((s) => {
    if (s.candidate) {
      seatHolders.set(s.candidate.id, {
        ordinal: s.position_ordinal,
        title: s.position_title,
      });
      seatedNames.set(s.candidate.id, s.candidate.name);
    }
  });

  const reloadCandidates = () => {
    if (!tenderId) return;
    setCandidatesLoading(true);
    fetchCandidatesV2(tenderId, selectedOrdinal ?? undefined)
      .then((data) => {
        setCandidates(data?.candidates ?? []);
      })
      .catch(() => setCandidates([]))
      .finally(() => setCandidatesLoading(false));
  };

  // Restore a removed candidate back to the main list, then refetch.
  const handleRestoreCandidate = async (candidateId: string) => {
    if (!tenderId) return;
    try {
      await restoreCandidateV2(tenderId, candidateId);
      reloadCandidates();
    } catch {
      // Leave the list as-is on failure; the recruiter can retry.
    }
  };

  // Refetch the data that depends on the active skill set after a criterion
  // add/activate/deactivate. The backend mirrors the criterion change onto the
  // tender's matching skills, so the candidate match % (and the extracted-skill
  // chips) only reflect it once we re-read them.
  const reloadMatching = () => {
    if (!tenderId) return;
    fetchTenderV2(tenderId)
      .then((data) => {
        if (data) {
          setTender((prev) =>
            prev ? { ...prev, skills: data.skills, criteria: data.criteria } : data,
          );
        }
      })
      .catch(() => {});
    reloadCandidates();
  };

  // Replace a single criterion in the loaded tender after an activate/deactivate,
  // then refetch the match-dependent data.
  const handleCriterionUpdated = (updated: CriterionAPIV2) => {
    setTender((prev) =>
      prev
        ? { ...prev, criteria: prev.criteria.map((c) => (c.id === updated.id ? updated : c)) }
        : prev,
    );
    reloadMatching();
  };

  // Append a recruiter-added criterion to the loaded tender's list, then refetch
  // the match-dependent data (the new criterion's skills shift the match %).
  const handleCriterionAdded = (added: CriterionAPIV2) => {
    setTender((prev) =>
      prev
        ? { ...prev, criteria: [...prev.criteria, added] }
        : prev,
    );
    reloadMatching();
  };

  // Kick off a re-extraction. Destructive (regenerates extracted criteria and
  // skills) and async, so we confirm first, then surface a status note. The new
  // results land minutes later — the recruiter reloads to see them.
  const handleReextract = async () => {
    if (!tenderId || reextracting) return;
    const confirmed = window.confirm(
      'Re-extract this tender?\n\nExtracted criteria and skills will be regenerated from the '
        + 'original content. Recruiter-added criteria are kept. This can take a few minutes.',
    );
    if (!confirmed) return;

    setReextracting(true);
    setReextractNote(null);
    try {
      const started = await reextractTenderV2(tenderId);
      setReextractNote(
        started
          ? 'Re-extraction started — new criteria and skills will appear in a few minutes. Reload the page to check.'
          : 'Tender not found — could not start re-extraction.',
      );
    } catch {
      setReextractNote('Could not start re-extraction. Please try again.');
    } finally {
      setReextracting(false);
    }
  };

  // Toggle a skill's active state (recruiter override, independent of criteria).
  // Optimistic for snappiness, then refetch so the match % reflects the change;
  // reverts on failure.
  const handleSkillToggle = async (skillId: string, nextActive: boolean, reason?: string) => {
    if (!tenderId) return;
    const flip = (active: boolean) =>
      setTender((prev) =>
        prev
          ? { ...prev, skills: prev.skills.map((s) => (s.id === skillId ? { ...s, active } : s)) }
          : prev,
      );
    flip(nextActive);
    try {
      await setTenderSkillActiveV2(tenderId, skillId, nextActive, reason);
      reloadMatching();
    } catch {
      flip(!nextActive);
    }
  };

  // Tender UUID exists in the URL but the backend returned 404 → full 404 page.
  if (!loading && notFound) {
    return <NotFound />;
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      {!loading && (
        <nav aria-label="Breadcrumb" className="text-sm text-gray-500">
          <ol className="flex items-center gap-2">
            <li><Link to="/v2/tenders" className="hover:text-brand-700">Tenders</Link></li>
            <li aria-hidden="true">/</li>
            <li className="text-gray-800 font-medium truncate max-w-xs">
              {tender?.title || 'Not found'}
            </li>
          </ol>
        </nav>
      )}

      {/* Tender header tile — title, deadline, extracted skills */}
      {!loading && tender && (() => {
        // Defensive fallbacks — guard against an older backend or partial response.
        const skills = Array.isArray(tender.skills) ? tender.skills : [];
        const deadline = tender.submission_deadline ?? null;
        const criteria = Array.isArray(tender.criteria) ? tender.criteria : [];

        // Grouped by `nature` (backend enrichment record), not the legacy `kind`
        // — which is null on everything extracted since V106.
        const requirements = criteria.filter((c) => c.nature === 'REQUIREMENT');
        const responsibilities = criteria.filter((c) => c.nature === 'RESPONSIBILITY');

        const reqActive = requirements.filter((c) => c.is_active).length;
        const reqTotal = requirements.length;
        const reqInactive = reqTotal - reqActive;

        const respActive = responsibilities.filter((c) => c.is_active).length;
        const respTotal = responsibilities.length;
        const respInactive = respTotal - respActive;

        // Removed candidates ("crossed-off suspects") are split out of the main
        // list and surfaced in the left rail; everything else is "active".
        const activeCandidates = (candidates ?? []).filter((c) => !c.removed);
        const removedCandidates = (candidates ?? []).filter((c) => c.removed);

        // Dismissed = ruled out by the analysis on a required qualification
        // (ELIMINATED band, matching_model.md §5). Split out of the main list the
        // same way removed candidates are, but derived from the band each read
        // rather than stored: user removal takes precedence (already excluded
        // above), and if re-analysis later clears the gate a dismissed candidate
        // returns to the list automatically — so there is no restore action.
        const dismissedCandidates = activeCandidates.filter(
          (c) => c.analysis?.band === 'ELIMINATED',
        );
        const listCandidates = activeCandidates.filter(
          (c) => c.analysis?.band !== 'ELIMINATED',
        );

        // Role card values.
        const candidatesLoaded = !candidatesLoading && candidates !== null;
        const candidateCount = activeCandidates.length;
        const analyzedCount = activeCandidates.filter((c) => c.analysis != null).length;
        // "Strong fits" = candidates the analysis placed in the top fit band
        // (Compelling — matching_model.md §5). A band only exists post-analysis.
        const compellingCount = activeCandidates.filter(
          (c) => c.analysis?.band === 'COMPELLING',
        ).length;
        const roleTitle = tender.requested_role || tender.title || 'Role';

        // Detected positions (Phase 1). Fall back to a single synthetic row from
        // the primary title when the backend hasn't detected any yet (older
        // backend, or before detection runs), preserving the single-role view.
        const detectedPositions = Array.isArray(tender.positions) ? tender.positions : [];
        const positions: PositionAPIV2[] = detectedPositions.length > 0
          ? [...detectedPositions].sort((a, b) => a.ordinal - b.ordinal)
          : [{ title: roleTitle, count: 1, ordinal: 0, persona_label: null, persona_custom: false }];
        const singlePosition = positions.length <= 1;

        // Criterion→position attachment (Phase 2). A criterion lists the ordinals
        // of the roles it is bound to. The roles rail shows the per-role criterion
        // count once attachment has produced links; recruiter triage of held-out
        // criteria lives in the "Needs a role" tab on the criteria rail (below),
        // not here. Before any links exist, every criterion has empty ordinals, so
        // the rail falls back to the tender-wide breakdown.
        const ordinalsOf = (c: (typeof criteria)[number]): number[] =>
          Array.isArray(c.position_ordinals) ? c.position_ordinals : [];
        const reqRespCriteria = criteria.filter(
          (c) => c.nature === 'REQUIREMENT' || c.nature === 'RESPONSIBILITY',
        );
        // Computed from the FULL criterion set, matching CriteriaRail. Attachment
        // links exist independently of whether a criterion has a nature, so
        // narrowing this to reqRespCriteria let one empty filter switch off the
        // whole roles rail: the per-role cards render as inert <div>s rather than
        // buttons when this is false, so the recruiter could not select a role at
        // all. A gate on presentation must not depend on a different stage's field.
        const hasAttachmentData = criteria.some((c) => ordinalsOf(c).length > 0);
        const countForOrdinal = (ordinal: number): number =>
          reqRespCriteria.filter((c) => ordinalsOf(c).includes(ordinal)).length;

        // Selected role drives the overview: the header subtitle becomes the role,
        // and the skills tile filters to the role's skills (the union of skill_ids
        // over its criteria — skills-for-role spec §5). No selection → tender-wide.
        const selectedRole =
          selectedOrdinal === null
            ? null
            : positions.find((p) => p.ordinal === selectedOrdinal) ?? null;
        const headerRole = selectedRole?.title ?? tender.requested_role;
        const roleSkillIds =
          selectedOrdinal === null
            ? null
            : new Set(
                criteria
                  .filter((c) => ordinalsOf(c).includes(selectedOrdinal))
                  .flatMap((c) => (Array.isArray(c.skill_ids) ? c.skill_ids : [])),
              );
        const shownSkills = roleSkillIds ? skills.filter((s) => roleSkillIds.has(s.id)) : skills;

        return (
          <>
            <section className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <h1 className="text-2xl font-bold text-gray-900">{tender.title || 'Not found'}</h1>
                  {headerRole && (
                    <p className="mt-1 text-sm font-medium text-gray-600">
                      {headerRole}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={handleReextract}
                  disabled={reextracting}
                  title="Regenerate extracted criteria and skills from the original content. Recruiter-added criteria are kept."
                  className="shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-gray-700 text-sm font-medium px-3 py-1.5"
                >
                  <span aria-hidden="true" className={reextracting ? 'animate-spin' : ''}>⟳</span>
                  <span>{reextracting ? 'Re-extracting…' : 'Re-extract'}</span>
                </button>
              </div>

              {reextractNote && (
                <p className="mt-2 text-xs text-brand-700">{reextractNote}</p>
              )}

              <dl className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-gray-700">
                <div className="flex items-center gap-1.5">
                  <span className="text-gray-400" aria-hidden="true">📅</span>
                  <span>Deadline: <strong>{formatDeadline(deadline)}</strong></span>
                </div>
              </dl>

              <div className="mt-4 pt-4 border-t border-gray-100 space-y-3">
                {selectedRole && (
                  <p className="text-[11px] text-gray-400">
                    Skills for <span className="font-medium text-gray-600">{selectedRole.title}</span>
                  </p>
                )}
                <SkillRow
                  label="Active skills"
                  items={shownSkills.filter((s) => s.active)}
                  emptyText="No active skills"
                  tone="active"
                  onToggle={handleSkillToggle}
                />
                <SkillRow
                  label="Inactive skills"
                  items={shownSkills.filter((s) => !s.active)}
                  emptyText="No inactive skills"
                  tone="inactive"
                  onToggle={handleSkillToggle}
                />
              </div>
            </section>

            {/* Stat tiles row — Requirements / Responsibilities from tender data;
                Candidates / Strong fits from the /v2/tenders/:id/candidates read.
                "Strong fits" counts the Compelling band (matching_model.md §5). */}
            <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatTile
                label="Requirements"
                icon="📋"
                big={String(reqActive)}
                small={`of ${reqTotal}`}
                subtext={
                  reqTotal === 0
                    ? undefined
                    : reqInactive > 0
                      ? `${reqInactive} deselected`
                      : 'all active'
                }
                subtextTone={reqInactive > 0 ? 'attention' : 'neutral'}
              />
              <StatTile
                label="Responsibilities"
                icon="⚙"
                big={String(respActive)}
                small={`of ${respTotal}`}
                subtext={
                  respTotal === 0
                    ? undefined
                    : respInactive > 0
                      ? `${respInactive} deselected`
                      : 'all active'
                }
                subtextTone={respInactive > 0 ? 'attention' : 'neutral'}
              />
              <StatTile
                label="Candidates"
                icon="👤"
                big={candidatesLoading || candidates === null ? '—' : String(listCandidates.length)}
                subtext={
                  candidatesLoading || candidates === null
                    ? undefined
                    : `${analyzedCount} analyzed${dismissedCandidates.length > 0 ? ` · ${dismissedCandidates.length} dismissed` : ''}${removedCandidates.length > 0 ? ` · ${removedCandidates.length} removed` : ''}`
                }
              />
              <StatTile
                label="Strong fits"
                icon="★"
                big={candidatesLoading || candidates === null ? '—' : String(compellingCount)}
                subtext={
                  candidatesLoading || candidates === null
                    ? undefined
                    : analyzedCount === 0
                      ? 'awaiting analysis'
                      : `of ${analyzedCount} analyzed`
                }
              />
            </section>

            {/* Working area: roles rail | candidates | criteria rail */}
            <div className="grid grid-cols-12 gap-5">
              {/* Roles in this tender — the detected position set (Phase 1). One
                  row for a single-role tender; the list with headcounts for a
                  multi-role tender. Per-role filtering of candidates/criteria is
                  a later phase. */}
              <div className="col-span-12 lg:col-span-3 flex flex-col gap-4 self-start">
              <aside className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                  <h2 className="text-sm font-semibold text-gray-900">Roles in this tender</h2>
                  <span className="text-xs text-gray-400">{positions.length} detected</span>
                </div>
                {hasAttachmentData && (
                  <p className="px-4 pt-3 -mb-1 text-[11px] text-gray-400">
                    Select a role to see its requirements, responsibilities &amp; skills.
                  </p>
                )}
                <ul className="p-3 space-y-1.5">
                  {positions.map((position) => {
                    const attached = hasAttachmentData ? countForOrdinal(position.ordinal) : null;
                    const selected = selectedOrdinal === position.ordinal;
                    // Roles are clickable once attachment has run — selecting one
                    // filters the criteria rail to that role. Before then there is
                    // no per-role data, so the rows are display-only.
                    const body = (
                      <>
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-sm font-semibold text-gray-900 leading-snug">
                            {position.title}
                          </p>
                          {position.count > 1 && (
                            <span
                              className="shrink-0 rounded-full bg-gray-100 text-gray-600 text-[11px] font-medium px-2 py-0.5"
                              title={`${position.count} needed`}
                            >
                              ×{position.count}
                            </span>
                          )}
                        </div>
                        {hasAttachmentData ? (
                          <p className="text-[11px] text-gray-500 mt-1">
                            {attached} {attached === 1 ? 'criterion' : 'criteria'}
                          </p>
                        ) : (
                          singlePosition && (
                            <>
                              <p className="text-[11px] text-gray-500 mt-1">
                                {reqActive} requirements · {respActive} responsibilities
                              </p>
                              {candidatesLoaded && (
                                <p className="text-[11px] text-gray-500">
                                  {candidateCount} candidates · {analyzedCount} analyzed
                                </p>
                              )}
                            </>
                          )
                        )}
                        {/* Seat line — who the proposal seated here, with the
                            band and a mark where the recruiter changed it
                            (team_proposal_spec §7, §11). Shown only on a
                            multi-position tender that got a team. */}
                        {team?.outcome === 'TEAM_PROPOSED' && (() => {
                          // One row per chair the tender asks for, not per role:
                          // a tier wanting two people with one seated is a role
                          // with a gap, and showing a single name would hide the
                          // shortfall the bid turns on.
                          const chairs = team.seats.filter(
                            (x) => x.position_ordinal === position.ordinal,
                          );
                          if (chairs.length === 0) return null;
                          const filled = chairs.filter((c) => c.candidate).length;
                          // Gaps are per empty chair and carry no chair index —
                          // the chairs are interchangeable — so they are taken in
                          // order as the empty rows are rendered.
                          const gaps = (team.feasibility?.seat_gaps ?? []).filter(
                            (g) => g.position_ordinal === position.ordinal,
                          );
                          let emptySoFar = -1;
                          return (
                            <div className="mt-1.5 pt-1.5 border-t border-dashed border-gray-200 space-y-1">
                              {chairs.length > 1 && (
                                <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                                  {t('teamProposal.seatedOf', '{{filled}} of {{total}} seated', {
                                    filled,
                                    total: chairs.length,
                                  })}
                                </p>
                              )}
                              {chairs.map((seat, index) => {
                                const band = seat.candidate?.analysis?.band ?? null;
                                if (!seat.candidate) emptySoFar += 1;
                                return (
                                  <div
                                    key={seat.candidate?.id ?? `empty-${position.ordinal}-${index}`}
                                    className="flex items-center gap-1.5 flex-wrap"
                                  >
                                    {seat.candidate ? (
                                      <>
                                        <span className="text-xs font-semibold text-gray-900">
                                          {seat.candidate.name}
                                        </span>
                                        {band && (
                                          <span
                                            className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-bold ${SEAT_BAND_CLS[band]}`}
                                          >
                                            {band}
                                          </span>
                                        )}
                                        {seat.source === 'RECRUITER' && (
                                          <span
                                            className="inline-flex items-center rounded border border-purple-200 bg-purple-50 px-1.5 py-0.5 text-[10px] font-semibold text-purple-700"
                                            title={t(
                                              'teamProposal.yoursHint',
                                              'You changed this seat — the tool proposed someone else.',
                                            )}
                                          >
                                            {t('teamProposal.yours', 'yours')}
                                          </span>
                                        )}
                                      </>
                                    ) : (
                                      <span className="text-xs italic text-rose-700">
                                        {(() => {
                                          // §5 separates "nobody fits" from
                                          // "everybody who fits is seated
                                          // elsewhere". Only the second is
                                          // actionable, and naming who holds
                                          // them is what makes it so.
                                          const gap = gaps[emptySoFar];
                                          if (gap?.reason === 'NO_ELIGIBLE_CANDIDATE') {
                                            return t(
                                              'teamProposal.noPlausible',
                                              'no plausible candidate',
                                            );
                                          }
                                          const who = (gap?.held_elsewhere ?? [])
                                            .map((id) => seatedNames.get(id) ?? id)
                                            .join(', ');
                                          return who
                                            ? t(
                                                'teamProposal.heldElsewhereNames',
                                                'held elsewhere — {{who}}',
                                                { who },
                                              )
                                            : t('teamProposal.seatEmpty', 'seat empty');
                                        })()}
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })()}
                      </>
                    );
                    return (
                      <li key={position.ordinal}>
                        {hasAttachmentData ? (
                          <button
                            type="button"
                            onClick={() =>
                              setSelectedOrdinal(selected ? null : position.ordinal)
                            }
                            aria-pressed={selected}
                            className={`w-full text-left px-3 py-3 rounded-lg border transition cursor-pointer ${
                              selected
                                ? 'bg-brand-100 border-brand-300 ring-1 ring-brand-300'
                                : 'bg-white border-gray-200 hover:border-brand-300'
                            }`}
                          >
                            {body}
                          </button>
                        ) : (
                          <div className="px-3 py-3 rounded-lg border bg-white border-gray-200">
                            {body}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>

                {/* Removed candidates — recruiter's crossed-off "suspects".
                    Restore returns them to the main list. */}
                <div className="px-5 py-4 border-t border-gray-100">
                  <h3 className="text-sm font-semibold text-gray-900">
                    Removed candidates{' '}
                    <span className="text-gray-400 font-normal">{removedCandidates.length}</span>
                  </h3>
                  {removedCandidates.length === 0 ? (
                    <p className="mt-2 text-[11px] text-gray-400 leading-snug">
                      Candidates you remove as not viable appear here, and can be restored.
                    </p>
                  ) : (
                    <ul className="mt-2 space-y-1.5">
                      {removedCandidates.map((c) => (
                        <li
                          key={c.id}
                          className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-gray-50 border border-gray-100"
                        >
                          <span className="text-xs text-gray-500 line-through truncate" title={c.name}>
                            {c.name}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleRestoreCandidate(c.id)}
                            title={`Restore ${c.name} to the candidate list`}
                            aria-label={`Restore ${c.name} to the candidate list`}
                            className="shrink-0 text-[11px] font-medium text-brand-600 hover:text-brand-700 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded"
                          >
                            Restore
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Dismissed candidates — ruled out by the analysis on a required
                    qualification (ELIMINATED band, matching_model.md §5). Split out
                    of the main list like removed candidates, but derived from the
                    band each read rather than stored: if re-analysis clears the gate
                    they return to the list automatically, so there is no restore. */}
                <div className="px-5 py-4 border-t border-gray-100">
                  <h3 className="text-sm font-semibold text-gray-900">
                    Dismissed{' '}
                    <span className="text-gray-400 font-normal">{dismissedCandidates.length}</span>
                  </h3>
                  {dismissedCandidates.length === 0 ? (
                    <p className="mt-2 text-[11px] text-gray-400 leading-snug">
                      Candidates the analysis rules out on a required qualification appear here.
                    </p>
                  ) : (
                    <ul className="mt-2 space-y-1.5">
                      {dismissedCandidates.map((c) => {
                        const unmet = c.analysis?.failed_must_haves ?? [];
                        return (
                          <li
                            key={c.id}
                            className="px-3 py-2 rounded-lg bg-rose-50/60 border border-rose-100"
                          >
                            <span className="block text-xs font-medium text-gray-600 truncate" title={c.name}>
                              {c.name}
                            </span>
                            {unmet.length > 0 ? (
                              <span
                                className="block mt-0.5 text-[11px] text-rose-600 leading-snug"
                                title={unmet.join('; ')}
                              >
                                Dismissed — unmet {unmet.length > 1 ? 'requirements' : 'requirement'}: {unmet.join('; ')}
                              </span>
                            ) : (
                              <span className="block mt-0.5 text-[11px] text-rose-600 leading-snug">
                                Dismissed — did not meet required qualifications
                              </span>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </aside>

              {/* Fulfilment — how the seated team answers what the tender asks
                  of the team as a whole, and what blocks a complete one
                  (team_proposal_spec §4, §5). Reports; never decides. */}
              {team && (
                <FulfilmentRail team={team} />
              )}
              </div>

              {/* Mid column — Candidates (default) | Tender (raw text).
                  The right rail stays visible on both tabs so the recruiter
                  can compare extracted criteria against where they came from. */}
              <section className="col-span-12 lg:col-span-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div role="tablist" className="inline-flex rounded-lg bg-white p-1 gap-1 text-xs">
                    <button
                      role="tab"
                      type="button"
                      aria-selected={midTab === 'candidates'}
                      onClick={() => setMidTab('candidates')}
                      className={`px-3 py-1.5 rounded-md font-medium ${
                        midTab === 'candidates'
                          ? 'bg-gray-100 text-gray-900'
                          : 'text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      Candidates{' '}
                      <span className="text-gray-400">
                        {candidatesLoading || candidates === null ? '—' : listCandidates.length}
                      </span>
                    </button>
                    <button
                      role="tab"
                      type="button"
                      aria-selected={midTab === 'tender'}
                      onClick={() => setMidTab('tender')}
                      className={`px-3 py-1.5 rounded-md font-medium ${
                        midTab === 'tender'
                          ? 'bg-gray-100 text-gray-900'
                          : 'text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      Tender
                    </button>
                  </div>
                  <div className="text-[11px] text-gray-400">
                    {midTab === 'candidates'
                      ? 'Verify the reading before pitching'
                      : 'Original tender content · read-only'}
                  </div>
                </div>

                {/* Candidates panel — band-first order from the backend
                    (per_position_matching_spec.md §15): Compelling → Credible →
                    Arguable, then not-yet-analyzed by skill, then eliminated. Only
                    candidates above the server-side cutoff appear here. */}
                {midTab === 'candidates' && (
                  <div className="space-y-4">
                    {!candidatesLoading && candidates !== null && (() => {
                      // Rendered in the order the backend returns
                      // (per_position_matching_spec.md §15): band-first —
                      // Compelling → Credible → Arguable, then not-yet-analyzed by
                      // skill, with skill proximity the within-band tie-breaker.
                      // Dismissed (eliminated) candidates are split into the rail
                      // above, so they never appear in this list. No client re-sort.
                      const sorted = listCandidates;
                      if (sorted.length === 0) {
                        return (
                          <div className="text-center py-8 text-gray-500 text-sm">
                            No candidates
                          </div>
                        );
                      }
                      return sorted.map((candidate) => (
                        <CandidateCard
                          key={candidate.id}
                          candidate={candidate}
                          tenderId={tenderId ?? ''}
                          onAnalyzed={reloadCandidates}
                          onRemoved={reloadCandidates}
                          team={
                            team?.outcome === 'TEAM_PROPOSED' && selectedOrdinal !== null
                              ? {
                                  seatedHere:
                                    seatHolders.get(candidate.id)?.ordinal === selectedOrdinal,
                                  heldAt:
                                    seatHolders.get(candidate.id) &&
                                    seatHolders.get(candidate.id)!.ordinal !== selectedOrdinal
                                      ? seatHolders.get(candidate.id)!
                                      : null,
                                  option:
                                    swapOptions.find((o) => o.employee_id === candidate.id) ?? null,
                                  positionOrdinal: selectedOrdinal,
                                  onChanged: () => setTeamNonce((n) => n + 1),
                                }
                              : undefined
                          }
                        />
                      ));
                    })()}
                  </div>
                )}

                {/* Tender panel — raw entries in ingestion order. Text payloads
                    show inline; binary payloads show metadata only. */}
                {midTab === 'tender' && (
                  <TenderRawPanel entries={tender.raw_entries ?? []} />
                )}
              </section>

              {/* Right column — criteria rail + add-criterion tile, stacked. */}
              <div className="col-span-12 lg:col-span-3 space-y-4 self-start">
                <CriteriaRail
                  tenderId={tenderId ?? ''}
                  criteria={criteria}
                  positions={positions}
                  selectedOrdinal={selectedOrdinal}
                  onClearSelectedOrdinal={() => setSelectedOrdinal(null)}
                  onCriterionUpdated={handleCriterionUpdated}
                />
                <AddCriterionTile
                  tenderId={tenderId ?? ''}
                  onCriterionAdded={handleCriterionAdded}
                />
              </div>
            </div>
          </>
        );
      })()}
    </div>
  );
}

/**
 * Human-readable byte size — kept light-touch so binary payload headers
 * stay scannable. KB threshold first since most attachments land there.
 */
function formatByteSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Prepend a small responsive stylesheet so the email body wraps to the
 * iframe's width instead of inducing horizontal scroll. Email HTML often
 * uses fixed-width tables, large inline images, and {@code <pre>} blocks
 * that don't soft-wrap — this CSS overrides those without altering the
 * email's intended styling.
 */
function wrapHtmlForIframe(html: string): string {
  // Force-wrap email content to the iframe width.
  // - Appended (not prepended) so our !important wins same-priority duels
  //   against styles in the email's own <head>.
  // - `* { white-space: normal !important }` is critical: many emails ship
  //   rules like `p, span, strong { white-space: pre }`, which prevents
  //   wrapping on every inline element. We override with normal, then
  //   re-enable pre-wrap on actual <pre>/<code> blocks (higher-specificity
  //   tag selector wins for those).
  const css = [
    'html, body { margin: 0; padding: 0; }',
    'body { padding: 12px; box-sizing: border-box; max-width: 100%; }',
    '* { max-width: 100% !important; box-sizing: border-box !important; word-break: break-word !important; overflow-wrap: anywhere !important; white-space: normal !important; }',
    '*[width] { width: auto !important; }',
    'img, video { height: auto !important; }',
    'table { table-layout: fixed !important; width: 100% !important; }',
    'pre, code { white-space: pre-wrap !important; }',
  ].join(' ');
  return `${html}<style>${css}</style>`;
}

function TenderRawPanel({ entries }: { entries: TenderRawEntryAPIV2[] }) {
  if (entries.length === 0) {
    return (
      <article className="bg-white rounded-xl border border-gray-200 px-6 py-10 text-center text-sm text-gray-500">
        No ingested content for this tender.
      </article>
    );
  }

  return (
    <div className="space-y-4">
      {entries.map((entry, idx) => {
        const isText = entry.content != null;
        const isHtml = isText && entry.mime_type.toLowerCase().startsWith('text/html');
        const displayName = entry.attachment_name || (isText ? 'Email body' : 'Attachment');
        return (
          <article
            key={`${entry.attachment_name ?? 'entry'}-${idx}`}
            className="bg-white rounded-xl border border-gray-200 overflow-hidden"
          >
            <header className="px-5 py-3 border-b border-gray-100 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">{displayName}</p>
                <p className="text-[11px] text-gray-500 mt-0.5 font-mono">
                  {entry.mime_type}
                  {entry.byte_size != null && (
                    <> · {formatByteSize(entry.byte_size)}</>
                  )}
                </p>
              </div>
              {!isText && (
                <span className="text-[11px] text-gray-400 shrink-0">📎 binary</span>
              )}
            </header>
            {isHtml ? (
              // Sandboxed iframe — renders the email's HTML/CSS faithfully without
              // letting scripts run, forms post, or styles leak into the app.
              // Empty sandbox attribute is intentional: most restrictive policy.
              // Note: <img>/CSS still load, so tracking pixels in the email body
              // will fire when the recruiter opens this tab.
              <iframe
                title={displayName}
                srcDoc={wrapHtmlForIframe(entry.content ?? '')}
                sandbox=""
                className="block w-full h-[640px] bg-white"
              />
            ) : isText ? (
              <div className="max-h-[640px] overflow-y-auto px-6 py-5">
                <pre className="text-[13px] text-gray-800 leading-relaxed whitespace-pre-wrap font-sans m-0">
                  {entry.content}
                </pre>
              </div>
            ) : (
              <div className="px-6 py-5 text-xs text-gray-500 italic">
                Binary content — not displayed inline.
              </div>
            )}
          </article>
        );
      })}
    </div>
  );
}
